import { useEffect, useState } from 'react'
import {
  RefreshCw, UserPlus, Loader2, CheckCircle2,
  ToggleLeft, ToggleRight, Briefcase, Star,
  ChevronDown, ChevronUp, AlertTriangle, Send,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import AdminLayout from '@/components/admin/AdminLayout'
import Button from '@/components/ui/Button'

// ── Helpers ──────────────────────────────────────────────────────────────────

const TRADE_OPTS = [
  { val: 'general',     label: 'General'     },
  { val: 'plumbing',    label: 'Plumbing'    },
  { val: 'electrical',  label: 'Electrical'  },
  { val: 'aircon',      label: 'Aircon'      },
  { val: 'carpentry',   label: 'Carpentry'   },
  { val: 'painting',    label: 'Painting'    },
  { val: 'masonry',     label: 'Masonry'     },
]

const TIER_OPTS = [
  { val: 'core',     label: 'Core'     },
  { val: 'extended', label: 'Extended' },
]

function statusBadge(status) {
  const map = {
    pending:          'bg-yellow-500/10 text-yellow-400 border-yellow-500/25',
    confirmed:        'bg-blue-500/10 text-blue-400 border-blue-500/25',
    assigned:         'bg-siglat-teal/10 text-siglat-teal border-siglat-teal/25',
    en_route:         'bg-purple-500/10 text-purple-400 border-purple-500/25',
    in_progress:      'bg-orange-500/10 text-orange-400 border-orange-500/25',
    pending_approval: 'bg-pink-500/10 text-pink-400 border-pink-500/25',
    completed:        'bg-green-500/10 text-green-400 border-green-500/25',
  }
  return map[status] ?? 'bg-surface-overlay text-ink-muted border-surface-border'
}

// ── Promote Customer → Worker panel ──────────────────────────────────────────

function PromotePanel({ onPromoted }) {
  const [email,   setEmail]   = useState('')
  const [trade,   setTrade]   = useState('general')
  const [tier,    setTier]    = useState('extended')
  const [acting,  setActing]  = useState(false)
  const [err,     setErr]     = useState(null)
  const [success, setSuccess] = useState(null)

  async function handlePromote() {
    if (!email.trim()) { setErr('Enter an email address.'); return }
    setErr(null); setSuccess(null); setActing(true)

    const { data: profiles, error: pErr } = await supabase
      .from('profiles')
      .select('id, full_name, role, email')
      .eq('email', email.trim().toLowerCase())
      .limit(1)

    if (pErr || !profiles?.length) {
      setErr('No account found with that email. They must sign in at least once first.')
      setActing(false); return
    }

    const profile = profiles[0]
    if (profile.role === 'admin') {
      setErr('This account is an admin — cannot demote to worker.')
      setActing(false); return
    }

    const { error: roleErr } = await supabase
      .from('profiles').update({ role: 'worker' }).eq('id', profile.id)
    if (roleErr) { setErr('Failed to update role.'); setActing(false); return }

    const { error: wpErr } = await supabase
      .from('worker_profiles')
      .upsert({ id: profile.id, trade, tier, is_available: false }, { onConflict: 'id' })
    if (wpErr) { setErr('Role updated but worker profile failed: ' + wpErr.message); setActing(false); return }

    setSuccess(`${profile.full_name} is now a worker (${trade}).`)
    setEmail(''); setActing(false); onPromoted()
  }

  return (
    <div className="bg-surface-raised border border-surface-border rounded-xl p-4 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <UserPlus className="w-4 h-4 text-siglat-teal" />
        <p className="text-sm font-semibold text-ink-primary">Promote Account to Worker</p>
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1.5">Email Address</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="worker@email.com"
            className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-siglat-teal"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1.5">Trade</label>
            <select value={trade} onChange={(e) => setTrade(e.target.value)}
              className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal">
              {TRADE_OPTS.map((o) => <option key={o.val} value={o.val}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1.5">Tier</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)}
              className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal">
              {TIER_OPTS.map((o) => <option key={o.val} value={o.val}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {err && (
        <div className="flex items-center gap-2 p-2.5 bg-siglat-red/10 border border-siglat-red/25 rounded-lg">
          <AlertTriangle className="w-3.5 h-3.5 text-siglat-red shrink-0" />
          <p className="text-xs text-siglat-red">{err}</p>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-2.5 bg-green-500/10 border border-green-500/25 rounded-lg">
          <CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" />
          <p className="text-xs text-green-400">{success}</p>
        </div>
      )}

      <Button onClick={handlePromote} disabled={acting} size="sm">
        {acting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Promoting…</> : <><UserPlus className="w-3.5 h-3.5" /> Promote to Worker</>}
      </Button>
    </div>
  )
}

// ── Worker Row ────────────────────────────────────────────────────────────────

function WorkerRow({ worker, onUpdated }) {
  const [expanded,  setExpanded]  = useState(false)
  const [trade,     setTrade]     = useState(worker.trade ?? 'general')
  const [tier,      setTier]      = useState(worker.tier  ?? 'extended')
  const [available, setAvailable] = useState(worker.is_available ?? false)
  const [saving,    setSaving]    = useState(false)
  const [err,       setErr]       = useState(null)
  const [dirty,     setDirty]     = useState(false)

  async function toggleAvailability() {
    const next = !available
    setAvailable(next)
    const { error } = await supabase
      .from('worker_profiles')
      .update({ is_available: next })
      .eq('id', worker.id)
    if (error) {
      console.error('Availability toggle failed:', error)
      setAvailable(!next) // revert UI
      setErr('Failed to update availability: ' + error.message)
    }
  }

  async function saveChanges() {
    setSaving(true); setErr(null)
    const { error } = await supabase
      .from('worker_profiles').upsert({ id: worker.id, trade, tier }, { onConflict: 'id' })
    if (error) { setErr('Save failed.'); setSaving(false); return }
    setDirty(false); setSaving(false); onUpdated()
  }

  async function demoteToCustomer() {
    if (!window.confirm(`Demote ${worker.full_name} to customer? They will lose worker access.`)) return
    setSaving(true)
    await supabase.from('profiles').update({ role: 'customer' }).eq('id', worker.id)
    setSaving(false); onUpdated()
  }

  return (
    <div className="bg-surface-raised border border-surface-border rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3.5 flex items-center justify-between gap-3 text-left hover:bg-surface-overlay transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <p className="text-sm font-semibold text-ink-primary">{worker.full_name}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium
              ${available
                ? 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25'
                : 'text-ink-muted bg-surface-overlay border-surface-border'}`}>
              {available ? 'Available' : 'Offline'}
            </span>
          </div>
          <p className="text-xs text-ink-muted">
            {worker.email ?? '—'} · {worker.trade ?? 'general'} · {worker.tier ?? 'extended'}
          </p>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-ink-muted">
            <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {worker.jobs_completed ?? 0} jobs</span>
            {worker.rating_avg > 0 && (
              <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {Number(worker.rating_avg).toFixed(1)}</span>
            )}
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-ink-muted shrink-0" /> : <ChevronDown className="w-4 h-4 text-ink-muted shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-surface-border px-4 py-4 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-ink-primary">Availability</p>
              <p className="text-xs text-ink-muted">{available ? 'Can receive job assignments' : 'Not accepting jobs'}</p>
            </div>
            <button onClick={toggleAvailability}>
              {available
                ? <ToggleRight className="w-9 h-9 text-siglat-teal" />
                : <ToggleLeft  className="w-9 h-9 text-ink-muted" />}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1.5">Trade</label>
              <select value={trade} onChange={(e) => { setTrade(e.target.value); setDirty(true) }}
                className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal">
                {TRADE_OPTS.map((o) => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1.5">Tier</label>
              <select value={tier} onChange={(e) => { setTier(e.target.value); setDirty(true) }}
                className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal">
                {TIER_OPTS.map((o) => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {err && <p className="text-xs text-siglat-red">{err}</p>}

          {dirty && (
            <Button size="sm" onClick={saveChanges} disabled={saving}>
              {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : 'Save Changes'}
            </Button>
          )}

          <button
            onClick={demoteToCustomer}
            className="text-xs text-siglat-red hover:underline underline-offset-2 self-start"
          >
            Remove worker access (demote to customer)
          </button>
        </div>
      )}
    </div>
  )
}

// ── Dispatch Tab — Assign Worker to Booking ───────────────────────────────────

function DispatchTab({ workers }) {
  const [bookings,   setBookings]   = useState([])
  const [loading,    setLoading]    = useState(true)
  const [assigning,  setAssigning]  = useState({}) // { [bookingId]: true }
  const [selected,   setSelected]   = useState({}) // { [bookingId]: workerId }
  const [done,       setDone]       = useState({}) // { [bookingId]: true }
  const [err,        setErr]        = useState({}) // { [bookingId]: msg }

  async function fetchUnassigned() {
    setLoading(true)

    // Fetch bookings that have no worker yet, across relevant statuses
    const { data: bks } = await supabase
      .from('bookings')
      .select('id, ref_number, status, address, customer_id, created_at')
      .is('worker_id', null)
      .not('status', 'in', '("completed","cancelled")')
      .order('created_at', { ascending: false })

    if (!bks?.length) { setBookings([]); setLoading(false); return }

    // Batch fetch customer names
    const custIds = [...new Set(bks.map((b) => b.customer_id).filter(Boolean))]
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', custIds)

    const profMap = {}
    for (const p of profiles ?? []) profMap[p.id] = p.full_name

    const enriched = bks.map((b) => ({
      ...b,
      _customerName: profMap[b.customer_id] ?? '—',
    }))

    setBookings(enriched)
    setLoading(false)
  }

  useEffect(() => { fetchUnassigned() }, []) // eslint-disable-line

  async function handleAssign(bookingId) {
    const workerId = selected[bookingId]
    if (!workerId) {
      setErr((p) => ({ ...p, [bookingId]: 'Select a worker first.' }))
      return
    }
    setAssigning((p) => ({ ...p, [bookingId]: true }))
    setErr((p) => ({ ...p, [bookingId]: null }))

    const { error } = await supabase
      .from('bookings')
      .update({ worker_id: workerId, status: 'assigned' })
      .eq('id', bookingId)

    if (error) {
      setErr((p) => ({ ...p, [bookingId]: 'Assignment failed: ' + error.message }))
      setAssigning((p) => ({ ...p, [bookingId]: false }))
      return
    }

    setDone((p) => ({ ...p, [bookingId]: true }))
    setAssigning((p) => ({ ...p, [bookingId]: false }))
    // Remove from list after short delay
    setTimeout(() => setBookings((prev) => prev.filter((b) => b.id !== bookingId)), 1200)
  }

  const availableWorkers = workers.filter((w) => w.is_available)

  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2].map((n) => (
          <div key={n} className="h-24 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
        ))}
      </div>
    )
  }

  if (!bookings.length) {
    return (
      <div className="bg-surface-raised border border-surface-border rounded-xl p-10 text-center">
        <CheckCircle2 className="w-8 h-8 text-siglat-teal mx-auto mb-2 opacity-50" />
        <p className="text-sm text-ink-muted">No unassigned bookings.</p>
        <p className="text-xs text-ink-muted mt-1">All active jobs have a worker assigned.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {availableWorkers.length === 0 && (
        <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/25 rounded-xl">
          <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0" />
          <p className="text-xs text-yellow-400">No workers are currently available. Toggle availability in the Workers tab.</p>
        </div>
      )}

      {bookings.map((booking) => {
        const isDone = done[booking.id]
        const isAssigning = assigning[booking.id]
        const selectedWorker = selected[booking.id]
        const errMsg = err[booking.id]

        return (
          <div key={booking.id} className={`bg-surface-raised border rounded-xl p-4 flex flex-col gap-3 transition-all
            ${isDone ? 'border-green-500/30' : 'border-surface-border'}`}>

            {/* Booking info */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <p className="text-sm font-semibold text-ink-primary">
                    {booking.ref_number ?? booking.id.slice(0, 8).toUpperCase()}
                  </p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${statusBadge(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>
                <p className="text-xs text-ink-muted">{booking._customerName}</p>
                <p className="text-xs text-ink-muted truncate mt-0.5">{booking.address ?? '—'}</p>
              </div>
              <p className="text-[10px] text-ink-muted shrink-0 mt-0.5">
                {new Date(booking.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })}
              </p>
            </div>

            {isDone ? (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                Worker assigned!
              </div>
            ) : (
              <>
                {/* Worker picker */}
                <div>
                  <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1.5">
                    Assign Worker
                  </label>
                  <select
                    value={selectedWorker ?? ''}
                    onChange={(e) => {
                      setSelected((p) => ({ ...p, [booking.id]: e.target.value }))
                      setErr((p) => ({ ...p, [booking.id]: null }))
                    }}
                    className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal"
                  >
                    <option value="">— Select worker —</option>
                    {workers.map((w) => (
                      <option key={w.id} value={w.id} disabled={!w.is_available}>
                        {w.full_name} · {w.trade ?? 'general'}
                        {!w.is_available ? ' (offline)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {errMsg && <p className="text-xs text-siglat-red">{errMsg}</p>}

                <Button
                  size="sm"
                  onClick={() => handleAssign(booking.id)}
                  disabled={isAssigning || !selectedWorker}
                >
                  {isAssigning
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Assigning…</>
                    : <><Send className="w-3.5 h-3.5" /> Assign &amp; Dispatch</>}
                </Button>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminWorkers() {
  const [tab,         setTab]        = useState('workers') // 'workers' | 'dispatch'
  const [workers,     setWorkers]    = useState([])
  const [loading,     setLoading]    = useState(true)
  const [refreshing,  setRefreshing] = useState(false)
  const [showPromote, setShowPromote] = useState(false)

  async function fetchWorkers() {
    setLoading(true)
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email, city')
      .eq('role', 'worker')
      .order('full_name')

    if (!profiles?.length) { setWorkers([]); setLoading(false); return }

    const ids = profiles.map((p) => p.id)
    const { data: wps } = await supabase
      .from('worker_profiles')
      .select('id, trade, tier, is_available, rating_avg, jobs_completed')
      .in('id', ids)

    const wpMap = {}
    for (const wp of wps ?? []) wpMap[wp.id] = wp

    setWorkers(profiles.map((p) => ({ ...p, ...(wpMap[p.id] ?? {}) })))
    setLoading(false)
  }

  useEffect(() => { fetchWorkers() }, []) // eslint-disable-line

  async function handleRefresh() {
    setRefreshing(true)
    await fetchWorkers()
    setRefreshing(false)
  }

  const available = workers.filter((w) => w.is_available).length

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-primary">Workers</h1>
            <p className="text-sm text-ink-muted mt-0.5">
              {workers.length} total · <span className="text-siglat-teal font-medium">{available} available</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 text-ink-muted hover:text-ink-primary hover:bg-surface-overlay rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {tab === 'workers' && (
              <Button size="sm" onClick={() => setShowPromote((v) => !v)}>
                <UserPlus className="w-3.5 h-3.5" />
                Add Worker
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-surface-overlay border border-surface-border rounded-xl mb-4">
          {[
            { id: 'workers',  label: 'Workers'  },
            { id: 'dispatch', label: 'Dispatch' },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors
                ${tab === id
                  ? 'bg-siglat-teal text-white'
                  : 'text-ink-muted hover:text-ink-primary'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Workers Tab */}
        {tab === 'workers' && (
          <>
            {showPromote && (
              <div className="mb-4">
                <PromotePanel onPromoted={() => { setShowPromote(false); fetchWorkers() }} />
              </div>
            )}

            {loading ? (
              <div className="flex flex-col gap-2">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="h-20 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
                ))}
              </div>
            ) : workers.length === 0 ? (
              <div className="bg-surface-raised border border-surface-border rounded-xl p-10 text-center">
                <Briefcase className="w-8 h-8 text-ink-muted mx-auto mb-2 opacity-30" />
                <p className="text-sm text-ink-muted">No workers yet.</p>
                <button onClick={() => setShowPromote(true)} className="mt-2 text-sm text-siglat-teal hover:underline">
                  Add your first worker
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {workers.map((w) => (
                  <WorkerRow key={w.id} worker={w} onUpdated={fetchWorkers} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Dispatch Tab */}
        {tab === 'dispatch' && (
          <DispatchTab workers={workers} />
        )}

      </div>
    </AdminLayout>
  )
}
